<template>
  <div class="gst-body">
    <div class="gst-header">
      <div>
        <p class="gst-header__title">Group Stores</p>
        <p class="gst-header__sub">All stores that are members of this group</p>
      </div>
    </div>

    <div v-if="stores.length > 0 || searchQuery || roleFilter" class="gst-filters">
      <hs-input v-model="searchQuery" icon="search" placeholder="Search by store name…" label="Search" />
      <hs-select v-model="roleFilter" label="Role" placeholder="All Roles" :options="roleOptions" />
      <div class="gst-filters__clear">
        <hs-button variant="ghost" icon="x" @click="clearFilters">Clear</hs-button>
      </div>
    </div>

    <div v-if="loading" class="gst-state">
      <span class="gst-spinner" />Loading stores…
    </div>

    <hs-alert v-else-if="error" tone="danger" :message="error">
      <template #default>
        {{ error }}
        <hs-button variant="ghost" size="sm" @click="loadStores">Retry</hs-button>
      </template>
    </hs-alert>

    <hs-empty-state
      v-else-if="stores.length === 0 && !searchQuery && !roleFilter"
      icon="shopping-bag"
      title="No stores yet"
      description="No stores are members of this group."
    />

    <hs-empty-state
      v-else-if="stores.length === 0"
      icon="search"
      title="No results"
      description="No stores match your filters."
    />

    <div v-else class="gst-table-area">
      <hs-table-shell title="Group stores" description="Stores connected to this group and their membership role.">
        <hs-data-table
          :headers="storeHeaders"
          :data="stores"
          :header-key-mapping="storeHeaderKeyMapping"
        >
          <template #cell-store="{ row }">
            <div class="gst-store-cell">
              <span class="gst-store-cell__name">{{ row.name || "Unnamed store" }}</span>
              <span class="gst-store-cell__meta">Store #{{ row.store_id }}</span>
            </div>
          </template>

          <template #cell-role="{ value }">
            <hs-badge :tone="getRoleTone(value)" size="sm">{{ value || "MEMBER" }}</hs-badge>
          </template>

          <template #cell-merchant="{ row }">
            <span class="gst-table__mono">{{ row.merchant_code || "-" }}</span>
          </template>

          <template #cell-joined="{ row }">
            {{ formatDate(row.created_at) }}
          </template>

          <template #cell-actions="{ row }">
            <hs-button
              v-if="canManageStores && row.role !== 'OWNER' && row.store_id !== storeId"
              variant="danger"
              icon="trash-2"
              size="sm"
              @click="confirmRemove(row)"
            >
              Remove
            </hs-button>
            <span v-else class="gst-table__muted">No actions</span>
          </template>
        </hs-data-table>
      </hs-table-shell>

      <hs-pagination
        v-if="pagination.total > 0"
        :current-page="pagination.page"
        :total-pages="pagination.pages"
        :total-items="pagination.total"
        :items-per-page="pagination.perPage"
        @previous-page="handlePreviousPage"
        @next-page="handleNextPage"
        @update:itemsPerPage="handlePerPageChange"
      />
    </div>

    <hs-confirm-dialog
      v-model="showRemoveConfirm"
      title="Remove Store from Group"
      :message="storeToRemove ? `Are you sure you want to remove '${storeToRemove.name}' from this group?` : ''"
      confirm-text="Remove"
      cancel-text="Cancel"
      tone="danger"
      :loading="removing"
      @confirm="removeStore"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import { HsButton, HsInput, HsSelect, HsBadge, HsAlert, HsEmptyState, HsConfirmDialog, HsPagination, HsDataTable, HsTableShell } from "@/components/ui";

export default {
  name: "GroupStoresTab",
  components: { HsButton, HsInput, HsSelect, HsBadge, HsAlert, HsEmptyState, HsConfirmDialog, HsPagination, HsDataTable, HsTableShell },
  props: {
    group: { type: Object, required: true },
    storeId: { type: Number, required: true },
  },
  emits: ["refresh"],
  setup(props, { emit }) {
    const store = useStore();
    const loading = ref(false);
    const error = ref("");
    const stores = ref([]);
    const storeToRemove = ref(null);
    const showRemoveConfirm = ref(false);
    const removing = ref(false);
    const searchQuery = ref("");
    const roleFilter = ref("");

    const pagination = ref({ page: 1, perPage: 10, total: 0, pages: 0, hasNext: false, hasPrev: false });
    const storeHeaders = ["Store", "Role", "Merchant", "Joined", "Actions"];
    const storeHeaderKeyMapping = {
      Store: "name",
      Role: "role",
      Merchant: "merchant_code",
      Joined: "created_at",
      Actions: "actions",
    };

    const roleOptions = computed(() => [
      { label: "Owner", value: "OWNER" },
      { label: "Admin", value: "ADMIN" },
      { label: "Member", value: "MEMBER" },
    ]);

    const canManageStores = computed(() =>
      store.getters["groups/canManageStores"] || store.getters["groups/isGroupOwner"]
    );

    const loadStores = async () => {
      loading.value = true;
      error.value = "";
      try {
        const params = { page: pagination.value.page, per_page: pagination.value.perPage };
        if (searchQuery.value) { params.search_query = searchQuery.value; params.search_by = "name"; }
        if (roleFilter.value) params.role = roleFilter.value;

        const response = await http.get(`/v2/groups/${props.group.id}/stores`, { params });
        const data = response.data || {};
        stores.value = data.items || [];
        pagination.value = {
          page: data.page || 1,
          perPage: data.per_page || 10,
          total: data.total || 0,
          pages: data.pages || 0,
          hasNext: data.has_next || false,
          hasPrev: data.has_prev || false,
        };
      } catch (err) {
        console.error("Failed to load stores", err);
        error.value = err?.response?.data?.error?.message || "Unable to load stores.";
      } finally {
        loading.value = false;
      }
    };

    const handlePreviousPage = () => { if (pagination.value.hasPrev) { pagination.value.page -= 1; loadStores(); } };
    const handleNextPage = () => { if (pagination.value.hasNext) { pagination.value.page += 1; loadStores(); } };
    const handlePerPageChange = (perPage) => { pagination.value.perPage = perPage; pagination.value.page = 1; loadStores(); };
    const clearFilters = () => { searchQuery.value = ""; roleFilter.value = ""; pagination.value.page = 1; };

    const confirmRemove = (storeObj) => {
      storeToRemove.value = storeObj;
      showRemoveConfirm.value = true;
    };

    const removeStore = async () => {
      if (!storeToRemove.value) return;
      removing.value = true;
      try {
        await http.delete(`/v2/groups/${props.group.id}/stores/${storeToRemove.value.store_id}`);
        toast.success("Store removed from group successfully");
        showRemoveConfirm.value = false;
        storeToRemove.value = null;
        await loadStores();
        emit("refresh");
      } catch (err) {
        console.error("Failed to remove store", err);
        toast.error(err?.response?.data?.error?.message || "Unable to remove store from group.");
      } finally {
        removing.value = false;
      }
    };

    const getRoleTone = (role) => {
      const tones = { OWNER: "warning", ADMIN: "primary", MEMBER: "success" };
      return tones[role] || "neutral";
    };

    const formatDate = (dateString) => {
      if (!dateString) return "Unknown";
      return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    watch([searchQuery, roleFilter], () => { pagination.value.page = 1; loadStores(); });
    onMounted(() => { loadStores(); });

    return {
      loading, error, stores, storeToRemove, showRemoveConfirm, removing,
      searchQuery, roleFilter, pagination, roleOptions, canManageStores,
      storeHeaders, storeHeaderKeyMapping,
      loadStores, handlePreviousPage, handleNextPage, handlePerPageChange, clearFilters,
      confirmRemove, removeStore, getRoleTone, formatDate,
    };
  },
};
</script>

<style scoped>
.gst-body {
  display: grid;
  gap: 1.1rem;
}

.gst-header {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
}

.gst-header__title {
  color: var(--hs-text);
  font-size: 0.95rem;
  font-weight: 750;
  margin: 0;
}

.gst-header__sub {
  color: var(--hs-muted);
  font-size: 0.8rem;
  margin: 0.15rem 0 0;
}

.gst-filters {
  align-items: flex-end;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr 1fr auto;
}

.gst-filters__clear {
  padding-bottom: 0.1rem;
}

@media (max-width: 520px) {
  .gst-filters { grid-template-columns: 1fr; }
}

.gst-state {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  font-size: 0.875rem;
  gap: 0.65rem;
  justify-content: center;
  padding: 2.5rem 0;
}

.gst-spinner {
  animation: gst-spin 0.8s linear infinite;
  border: 2px solid color-mix(in srgb, var(--hs-primary) 28%, transparent);
  border-radius: 50%;
  border-top-color: var(--hs-primary);
  display: inline-block;
  height: 1.1rem;
  width: 1.1rem;
}

@keyframes gst-spin { to { transform: rotate(360deg); } }

.gst-table-area {
  display: grid;
  gap: 0.85rem;
}

.gst-store-cell {
  display: grid;
  gap: 0.15rem;
  min-width: 0;
}

.gst-store-cell__name {
  color: var(--hs-text);
  font-size: 0.9rem;
  font-weight: 850;
}

.gst-store-cell__meta,
.gst-table__muted {
  color: var(--hs-muted);
  font-size: 0.75rem;
}

.gst-table__mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 0.8rem;
}
</style>
